// DataOps API service - connects to lens-dataops microservice
import {
  mockCatalogOverview,
  mockDatasets,
  mockJobs,
  mockNamespaces,
  mockLineageGraph,
  mockUpstreamNodes,
  mockDownstreamNodes,
  mockLineagePath,
  mockRunsOverview,
  mockRuns,
  mockEventsOverview,
  mockEvents,
  mockImpactAnalysis,
  mockImpactOverview,
  mockDependencies,
  delay,
  filterDatasets,
  filterJobs,
  filterRuns,
  filterEvents,
} from './dataopsMockData';
import { demoModeManager } from './demoModeManager';

const DATAOPS_API_BASE = process.env.REACT_APP_DATAOPS_API_BASE_URL;

// ==================== MOCK DATA CONFIGURATION ====================
// Returns true in demo mode (anonymous/assessment_only users) or when no API URL is configured
const USE_MOCK_DATA_STATIC = false;
const shouldUseMockData = (): boolean => {
  if (USE_MOCK_DATA_STATIC) return true;
  const { isDemoMode } = demoModeManager.getDemoMode();
  return isDemoMode;
};

/**
 * Get the selected client ID from localStorage (set by ClientContext)
 * Used by partner/aws/admin users who need to specify which client they're viewing
 */
const getSelectedClientId = (): string | null => {
  try {
    return localStorage.getItem('selectedClientId');
  } catch {
    return null;
  }
};

const getSelectedProjectId = (): string | null => {
  try {
    return localStorage.getItem('selectedProjectId');
  } catch {
    return null;
  }
};

const getGlobalFilter = (): { filterType: string; selectedId: string | null } | null => {
  try {
    const stored = localStorage.getItem('globalFilter');
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return null;
};

/**
 * Build query string from parameters object
 * @param {Object} params - Query parameters object
 * @returns {string} Query string
 */
const buildQueryString = (params: Record<string, any> = {}): string => {
  const queryParams = new URLSearchParams();
  const clientId = getSelectedClientId();

  // Always add client_id if available (for partner/aws users)
  if (clientId) {
    queryParams.append('client_id', clientId);
  }

  // Add filter based on global filter state (project_id, workload_id, or account_id)
  const globalFilter = getGlobalFilter();
  if (globalFilter?.selectedId) {
    if (globalFilter.filterType === 'project') {
      queryParams.append('project_id', globalFilter.selectedId);
    } else if (globalFilter.filterType === 'workload') {
      queryParams.append('workload_id', globalFilter.selectedId);
    } else if (globalFilter.filterType === 'account') {
      queryParams.append('account_id', globalFilter.selectedId);
    } else if (globalFilter.filterType === 'environment') {
      queryParams.append('environment_id', globalFilter.selectedId);
    }
  } else {
    // Backward compat: fall back to old selectedProjectId
    const projectId = getSelectedProjectId();
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
  }

  // Add other parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
};

interface DataopsApiError extends Error {
  data?: unknown;
}

/**
 * Make authenticated API call to dataops service
 */
async function dataopsApiCall(endpoint: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');

  const response = await fetch(`${DATAOPS_API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
    if (typeof errorBody.detail === 'object' && errorBody.detail !== null) {
      const err: DataopsApiError = new Error(errorBody.detail.message || `HTTP ${response.status}`);
      err.data = errorBody.detail;
      throw err;
    }
    throw new Error(errorBody.detail || `HTTP ${response.status}`);
  }

  // Handle 204 No Content responses
  if (response.status === 204) {
    return null;
  }

  return response.json();
}


// ==================== Catalog API ====================

/**
 * Get catalog overview statistics
 * @param {Object} params - Query parameters
 * @param {string} params.client_id - Optional client ID filter
 * @returns {Promise<Object>} Overview statistics
 */
// Get active filter params for demo mode (reads global filter state)
const getDemoFilterParams = (): Record<string, string> => {
  const filter = getGlobalFilter();
  if (filter?.selectedId) {
    if (filter.filterType === 'project') return { project_id: filter.selectedId };
    if (filter.filterType === 'workload') return { workload_id: filter.selectedId };
    if (filter.filterType === 'account') return { account_id: filter.selectedId };
    if (filter.filterType === 'environment') return { environment_id: filter.selectedId };
  }
  // Backward compat
  const projectId = getSelectedProjectId();
  if (projectId) return { project_id: projectId };
  return {};
};

// Filter mock items by demo filter params
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyDemoFilter = (items: any[]): any[] => {
  const params = getDemoFilterParams();
  let filtered = items;
  if (params.project_id) {
    filtered = filtered.filter((item: any) => item.project_id === params.project_id);
  }
  if (params.workload_id) {
    filtered = filtered.filter((item: any) => item.workload_id === params.workload_id);
  }
  if (params.account_id) {
    filtered = filtered.filter((item: any) => item.account_id === params.account_id || item.cloud_account_id === params.account_id);
  }
  if (params.environment_id) {
    filtered = filtered.filter((item: any) => item.environment_id === params.environment_id);
  }
  return filtered;
};

export const catalogAPI = {
  getOverview: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const datasets = applyDemoFilter(mockDatasets);
      const jobs = applyDemoFilter(mockJobs);
      return {
        ...mockCatalogOverview,
        total_datasets: datasets.length,
        total_jobs: jobs.length,
        active_jobs: jobs.filter((job) => job.status === 'running').length,
        namespaces_count: new Set(datasets.map((dataset) => dataset.namespace)).size,
        quality_score: datasets.length > 0 ? Number((datasets.reduce((sum, dataset) => sum + (dataset.quality_score || 0), 0) / datasets.length).toFixed(1)) : 0,
        degraded_datasets: datasets.filter((dataset) => dataset.freshness_status === 'stale' || (dataset.quality_score || 0) < 80).length,
      };
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/catalog/overview${queryString}`);
  },

  /**
   * List datasets with optional filters
   * @param {Object} filters - Filter parameters
   * @param {string} filters.namespace - Filter by namespace
   * @param {string} filters.source - Filter by data source type
   * @param {string} filters.search - Search datasets by name or description
   * @returns {Promise<Array>} List of datasets
   */
  getDatasets: async (filters: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const demoParams = getDemoFilterParams();
      return filterDatasets(mockDatasets, { ...filters, ...demoParams });
    }
    const queryString = buildQueryString(filters);
    return dataopsApiCall(`/catalog/datasets${queryString}`);
  },

  /**
   * Get dataset by ID
   * @param {string} id - Dataset ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Dataset object
   */
  getDataset: async (id: string, params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const dataset = mockDatasets.find(d => d.id === id);
      if (!dataset) {
        throw new Error(`Dataset with id ${id} not found`);
      }
      return dataset;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/catalog/datasets/${id}${queryString}`);
  },

  /**
   * List jobs with optional filters
   * @param {Object} filters - Filter parameters
   * @param {string} filters.namespace - Filter by namespace
   * @param {string} filters.status - Filter by job status
   * @param {string} filters.search - Search jobs by name or description
   * @returns {Promise<Array>} List of jobs
   */
  getJobs: async (filters: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const demoParams = getDemoFilterParams();
      return filterJobs(mockJobs, { ...filters, ...demoParams });
    }
    const queryString = buildQueryString(filters);
    return dataopsApiCall(`/catalog/jobs${queryString}`);
  },

  /**
   * Get job by ID
   * @param {string} id - Job ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Job object
   */
  getJob: async (id: string, params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const job = mockJobs.find(j => j.id === id);
      if (!job) {
        throw new Error(`Job with id ${id} not found`);
      }
      return job;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/catalog/jobs/${id}${queryString}`);
  },

  /**
   * List namespaces
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of namespaces
   */
  getNamespaces: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return mockNamespaces;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/catalog/namespaces${queryString}`);
  },

  /**
   * Trigger a data quality check for a dataset
   * @param {string} datasetId - Dataset ID
   * @param {string} environmentId - AWS environment ID for Glue access
   * @returns {Promise<Object>} { run_id, status }
   */
  runDataQuality: async (datasetId: string, environmentId: string): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return { run_id: 'mock-dq-run-001', status: 'pending' };
    }
    const queryString = buildQueryString();
    return dataopsApiCall(`/catalog/datasets/${datasetId}/run-quality${queryString}`, {
      method: 'POST',
      body: JSON.stringify({ environment_id: environmentId }),
    });
  },

  /**
   * Get data quality results for a dataset
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Array>} List of quality results
   */
  getQualityResults: async (datasetId: string, params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const { mockQualityResults } = await import('./dataopsMockData');
      return (mockQualityResults as Record<string, any>)[datasetId] || [];
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/catalog/datasets/${datasetId}/quality-results${queryString}`);
  },

  /**
   * Get latest data quality status for a dataset (polling)
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} { status, score, completed_at }
   */
  getQualityStatus: async (datasetId: string, params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return { status: 'none' };
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/catalog/datasets/${datasetId}/quality-status${queryString}`);
  },

  assignDatasetProject: async (datasetId: string, projectId: string | null): Promise<any> => {
    const queryString = buildQueryString();
    return dataopsApiCall(`/catalog/datasets/${datasetId}/project${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify({ project_id: projectId }),
    });
  },

  assignJobProject: async (jobId: string, projectId: string | null): Promise<any> => {
    const queryString = buildQueryString();
    return dataopsApiCall(`/catalog/jobs/${jobId}/project${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify({ project_id: projectId }),
    });
  },

  bulkAssignProject: async (ids: string[], entityType: 'dataset' | 'job', projectId: string | null): Promise<any> => {
    const queryString = buildQueryString();
    return dataopsApiCall(`/catalog/bulk-assign-project${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify({ ids, entity_type: entityType, project_id: projectId }),
    });
  },

  assignDatasetWorkload: async (datasetId: string, workloadId: string | null): Promise<any> => {
    const queryString = buildQueryString();
    return dataopsApiCall(`/catalog/datasets/${datasetId}/workload${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify({ workload_id: workloadId }),
    });
  },

  assignJobWorkload: async (jobId: string, workloadId: string | null): Promise<any> => {
    const queryString = buildQueryString();
    return dataopsApiCall(`/catalog/jobs/${jobId}/workload${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify({ workload_id: workloadId }),
    });
  },

  bulkAssignWorkload: async (ids: string[], entityType: 'dataset' | 'job', workloadId: string | null): Promise<any> => {
    const queryString = buildQueryString();
    return dataopsApiCall(`/catalog/bulk-assign-workload${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify({ ids, entity_type: entityType, workload_id: workloadId }),
    });
  },

  autoAssignProjects: async (): Promise<any> => {
    const queryString = buildQueryString();
    return dataopsApiCall(`/catalog/auto-assign-projects${queryString}`, {
      method: 'POST',
    });
  },
};

// ==================== Lineage API ====================

/**
 * Get lineage graph for a dataset or job
 * @param {Object} params - Query parameters
 * @param {string} params.dataset_id - Dataset ID to get lineage for
 * @param {string} params.job_id - Job ID to get lineage for
 * @param {number} params.hops - Number of hops to traverse (default: 2, min: 1, max: 10)
 * @returns {Promise<Object>} Lineage graph with nodes and edges
 */
export const lineageAPI = {
  getGraph: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return mockLineageGraph;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/lineage/graph${queryString}`);
  },

  /**
   * Get upstream lineage nodes
   * @param {Object} params - Query parameters
   * @param {string} params.node_id - Node ID to get upstream lineage for (required)
   * @param {string} params.node_type - Node type: 'dataset' or 'job' (required)
   * @param {number} params.max_hops - Maximum number of hops (default: 2, min: 1, max: 10)
   * @returns {Promise<Array>} List of upstream nodes
   */
  getUpstream: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return mockUpstreamNodes;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/lineage/upstream${queryString}`);
  },

  /**
   * Get downstream lineage nodes
   * @param {Object} params - Query parameters
   * @param {string} params.node_id - Node ID to get downstream lineage for (required)
   * @param {string} params.node_type - Node type: 'dataset' or 'job' (required)
   * @param {number} params.max_hops - Maximum number of hops (default: 2, min: 1, max: 10)
   * @returns {Promise<Array>} List of downstream nodes
   */
  getDownstream: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return mockDownstreamNodes;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/lineage/downstream${queryString}`);
  },

  /**
   * Get lineage path between two nodes
   * @param {Object} params - Query parameters
   * @param {string} params.source_node_id - Source node ID (required)
   * @param {string} params.target_node_id - Target node ID (required)
   * @param {number} params.max_hops - Maximum number of hops (default: 5, min: 1, max: 20)
   * @returns {Promise<Object>} Path object with nodes and hops count
   */
  getPath: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return mockLineagePath;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/lineage/path${queryString}`);
  },
};

// ==================== Runs API ====================

/**
 * Get runs overview statistics
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Overview statistics
 */
export const runsAPI = {
  getOverview: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const runs = applyDemoFilter(mockRuns);
      const successfulRuns = runs.filter((run) => run.status === 'success').length;
      const failedRuns = runs.filter((run) => run.status === 'failed').length;
      const runningRuns = runs.filter((run) => run.status === 'running').length;
      const pendingRuns = runs.filter((run) => run.status === 'pending').length;
      const completedRuns = successfulRuns + failedRuns;
      return {
        ...mockRunsOverview,
        total_runs: runs.length,
        success_rate: completedRuns > 0 ? Number(((successfulRuns / completedRuns) * 100).toFixed(1)) : 0,
        successful_runs: successfulRuns,
        failed_runs: failedRuns,
        running_runs: runningRuns,
        pending_runs: pendingRuns,
        completed: successfulRuns,
        failed: failedRuns,
        running: runningRuns,
        pending: pendingRuns,
      };
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/runs/overview${queryString}`);
  },

  /**
   * List pipeline runs with optional filters
   * @param {Object} filters - Filter parameters
   * @param {string} filters.job_id - Filter by job ID
   * @param {string} filters.status - Filter by run status
   * @param {string} filters.start_date - Filter by start date (datetime)
   * @param {string} filters.end_date - Filter by end date (datetime)
   * @param {number} filters.limit - Maximum number of runs to return (default: 100, min: 1, max: 1000)
   * @returns {Promise<Array>} List of pipeline runs
   */
  getRuns: async (filters: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const demoParams = getDemoFilterParams();
      const filtered = Object.keys(demoParams).length > 0 ? applyDemoFilter(mockRuns) : mockRuns;
      return filterRuns(filtered, filters);
    }
    const queryString = buildQueryString(filters);
    return dataopsApiCall(`/runs${queryString}`);
  },

  /**
   * Get pipeline run by ID
   * @param {string} id - Run ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Pipeline run object
   */
  getRun: async (id: string, params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const run = mockRuns.find(r => r.id === id);
      if (!run) {
        throw new Error(`Run with id ${id} not found`);
      }
      return run;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/runs/${id}${queryString}`);
  },

  removeRun: async (id: string): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return {};
    }
    const queryString = buildQueryString();
    return dataopsApiCall(`/runs/${id}/remove${queryString}`, { method: 'PATCH' });
  },
};

// ==================== Events API ====================

/**
 * Get events overview statistics
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Overview statistics
 */
export const eventsAPI = {
  getOverview: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const events = applyDemoFilter(mockEvents);
      const resolvedEvents = events.filter((event) => !['FAILED', 'WARNING'].includes(String(event.status || '').toUpperCase())).length;
      const failedJobs = events.filter((event) => String(event.status || '').toUpperCase() === 'FAILED').length;
      const runningJobs = events.filter((event) => String(event.status || '').toUpperCase() === 'RUNNING').length;
      return {
        ...mockEventsOverview,
        total_events: events.length,
        success_rate: events.length > 0 ? Number(((resolvedEvents / events.length) * 100).toFixed(1)) : 0,
        failed_jobs: failedJobs,
        running_jobs: runningJobs,
      };
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/events/overview${queryString}`);
  },

  /**
   * List pipeline events with optional filters
   * @param {Object} filters - Filter parameters
   * @param {string} filters.job_id - Filter by job ID
   * @param {string} filters.dataset_id - Filter by dataset ID
   * @param {string} filters.event_type - Filter by event type
   * @param {string} filters.status - Filter by event status
   * @param {string} filters.start_date - Filter by start date (datetime)
   * @param {string} filters.end_date - Filter by end date (datetime)
   * @param {number} filters.limit - Maximum number of events to return (default: 100, min: 1, max: 1000)
   * @returns {Promise<Array>} List of pipeline events
   */
  getEvents: async (filters: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const demoParams = getDemoFilterParams();
      const filtered = Object.keys(demoParams).length > 0 ? applyDemoFilter(mockEvents) : mockEvents;
      return filterEvents(filtered, filters);
    }
    const queryString = buildQueryString(filters);
    return dataopsApiCall(`/events${queryString}`);
  },

  /**
   * Get pipeline event by ID
   * @param {string} id - Event ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Pipeline event object
   */
  getEvent: async (id: string, params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      const event = mockEvents.find(e => e.id === id);
      if (!event) {
        throw new Error(`Event with id ${id} not found`);
      }
      return event;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/events/${id}${queryString}`);
  },
};

// ==================== Impact API ====================

/**
 * Get impact analysis for a dataset or job
 * @param {Object} params - Query parameters
 * @param {string} params.dataset_id - Dataset ID for impact analysis
 * @param {string} params.job_id - Job ID for impact analysis
 * @returns {Promise<Object>} Impact analysis with overview, dependencies, and message
 */
export const impactAPI = {
  getAnalysis: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return mockImpactAnalysis;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/impact/analysis${queryString}`);
  },

  /**
   * Get impact overview statistics
   * @param {Object} params - Query parameters
   * @param {string} params.dataset_id - Dataset ID for impact overview
   * @param {string} params.job_id - Job ID for impact overview
   * @returns {Promise<Object>} Impact overview statistics
   */
  getOverview: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return mockImpactOverview;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/impact/overview${queryString}`);
  },

  /**
   * Get downstream dependencies for a source entity
   * @param {Object} params - Query parameters
   * @param {string} params.source_id - Source entity ID (dataset or job) (required)
   * @param {string} params.source_type - Source entity type: 'dataset' or 'job' (required)
   * @returns {Promise<Array>} List of dependencies
   */
  getDependencies: async (params: Record<string, any> = {}): Promise<any> => {
    if (shouldUseMockData()) {
      await delay();
      return mockDependencies;
    }
    const queryString = buildQueryString(params);
    return dataopsApiCall(`/impact/dependencies${queryString}`);
  },
};
